import { useEffect, useRef } from 'react';
import { usePermissions } from '@customerio/hydra-permissions/react';
import { SafeNavigate } from '@/services/router/safe-navigate';
import type { Permission } from '@customerio/hydra-permissions';
import { PageLoader, useSnackbars } from '@customerio/pluma-components/react';
import { useAuth } from '@/services/auth';
import { usePermissionsResolved } from '@/services/permissions/resolved';
import { useParams } from '@/services/router/compat';

type RouteParams = Record<string, string | undefined>;

/** One id for every turned-away route, so a redirect chain says it once. */
const ACCESS_DENIED_SNACKBAR_ID = 'route.accessDenied';
/** Long enough to read after landing somewhere the customer didn't ask for. */
const ACCESS_DENIED_AUTO_CLOSE_MS = 10_000;

type ProtectedRouteBaseProps = {
  redirectTo: string | ((params: RouteParams) => string);
  children: React.ReactNode;
  errorMessage?: string;
  allowReadOnlyMasqAccess?: boolean;
};

/**
 * Pass `permission` to require one permission, or `anyPermissions` to require at
 * least one of several. A page that holds independent sections wants
 * `anyPermissions`, so a role that can reach one section keeps the page.
 */
type ProtectedRouteProps = ProtectedRouteBaseProps &
  ({ permission: Permission; anyPermissions?: never } | { permission?: never; anyPermissions: Permission[] });

/** Runs the check that the given prop form asks for. */
function usePermissionResult(permission: Permission | undefined, anyPermissions: Permission[] | undefined) {
  const { hasPermission, hasAnyPermission } = usePermissions();

  if (anyPermissions != null) {
    return hasAnyPermission(...anyPermissions);
  }
  if (permission != null) {
    return hasPermission(permission);
  }
  return false;
}

/** Whether the session masquerades at a level that grants at least read access. */
function useMasqReadAccess() {
  const auth = useAuth();

  if (auth.account?.is_masq !== true) {
    return false;
  }
  return auth.user?.canReadOnlyMasq === true || auth.user?.canReadWriteMasq === true || auth.user?.isSuperUser === true;
}

type AccessDecision = 'allowed' | 'pending' | 'denied';

/**
 * `pending` while the inputs the check reads are still loading. They answer `false`
 * until then, and the redirect cannot be taken back, so only a denial made on loaded
 * inputs sends the user away.
 */
function useAccessDecision(
  permission: Permission | undefined,
  anyPermissions: Permission[] | undefined,
  allowReadOnlyMasqAccess: boolean,
): AccessDecision {
  const permissionResult = usePermissionResult(permission, anyPermissions);
  const isMasqWithReadAccess = useMasqReadAccess();
  const isResolved = usePermissionsResolved();

  if (permissionResult || (allowReadOnlyMasqAccess && isMasqWithReadAccess)) {
    return 'allowed';
  }
  return isResolved ? 'denied' : 'pending';
}

/** Says why the user was turned away, once per denial. */
function useAccessDeniedSnackbar(isDenied: boolean, errorMessage: string) {
  const snackbars = useSnackbars();
  const hasShownSnackbarRef = useRef(false);

  useEffect(() => {
    if (isDenied) {
      if (!hasShownSnackbarRef.current) {
        snackbars.create({
          message: errorMessage,
          variant: 'critical',
          id: ACCESS_DENIED_SNACKBAR_ID,
          autoCloseTimeout: ACCESS_DENIED_AUTO_CLOSE_MS,
        });
        hasShownSnackbarRef.current = true;
      }
    } else {
      hasShownSnackbarRef.current = false;
    }
    // oxlint-disable-next-line react/exhaustive-deps
  }, [isDenied]);
}

export function ProtectedRoute({
  permission,
  anyPermissions,
  redirectTo,
  children,
  errorMessage = "You're not allowed to view this page. Contact an Admin to update your role.",
  allowReadOnlyMasqAccess = false,
}: ProtectedRouteProps) {
  const decision = useAccessDecision(permission, anyPermissions, allowReadOnlyMasqAccess);
  const params = useParams({ strict: false });
  useAccessDeniedSnackbar(decision === 'denied', errorMessage);

  if (decision === 'pending') {
    return <PageLoader />;
  }

  if (decision === 'denied') {
    const resolvedRedirectTo = typeof redirectTo === 'function' ? redirectTo(params) : redirectTo;
    return <SafeNavigate to={resolvedRedirectTo} options={{ shouldReplaceHistoryEntry: true }} />;
  }

  return <>{children}</>;
}
